import { toast } from "@overmap-ai/blocks"
import { RESET_PROJECT_STATE } from "@overmap-ai/core"
import { createFileRoute, Outlet } from "@tanstack/react-router"
import { sdk } from "src/sdk"
import { appStore, setActiveProjectId } from "src/store"
import { NotFound } from "src/views/ProjectsView/NotFound"

import { loadingToast } from "../../../../../components/Toasts"

/* Keep track of when the data has been loaded once throughout the lifetime of the application running,
 * this allows for the route loader to only load the data when the projectId path param has changed or the app is
 * refreshed */
let LOADED_ONCE = false

/* Manual AbortController management for deferred data loading.
 * TanStack Router's abortController only triggers when the loader promise resolves,
 * but with deferred data the loader returns immediately, so we manage our own. */
let PREV_ABORT_CONTROLLER: AbortController | null = null

export const Route = createFileRoute("/_loggedIn/$organizationId/_map/_layout/$projectId")({
	component: Outlet,
	notFoundComponent: NotFound,
	gcTime: 0,
	preload: false,
	pendingMs: 0,
	shouldReload: ({ cause }) => {
		return cause === "enter"
	},
	loader: async ({ params, context }) => {
		const { projectId } = params
		const { activeProjectId } = context

		console.debug("$projectId loader running: ", projectId)

		if (LOADED_ONCE && activeProjectId === projectId) return

		// Abort any in-flight request from a previous navigation
		if (PREV_ABORT_CONTROLLER) PREV_ABORT_CONTROLLER.abort()

		// Create a new AbortController for this request
		const abortController = new AbortController()
		PREV_ABORT_CONTROLLER = abortController

		LOADED_ONCE = true

		appStore.dispatch(setActiveProjectId(projectId))
		if (activeProjectId !== projectId) appStore.dispatch(RESET_PROJECT_STATE())

		// READY TO START PROJECT DATA FETCH
		// 1) Fetch and await project data expecting to be awaited
		// 2) Fetch and allow lazy project data to resolve when it does

		const fetchLazyProjectData = await sdk.main.fetchProjectData(projectId, abortController.signal)

		const lazyProjectData = fetchLazyProjectData()

		let toastId: ReturnType<typeof loadingToast> | undefined

		const timeout = setTimeout(() => {
			toastId = loadingToast({
				id: `FETCH_PROJECT_DATA-${projectId}`,
				description: "Loading project data for offline use",
			})
		}, 2000)

		const handleAbort = () => {
			toast.dismiss(toastId)
		}

		abortController.signal?.addEventListener("abort", handleAbort)

		void lazyProjectData.finally(() => {
			toast.dismiss(toastId)
			clearTimeout(timeout)
			abortController.signal?.removeEventListener("abort", handleAbort)
			if (PREV_ABORT_CONTROLLER === abortController) {
				PREV_ABORT_CONTROLLER = null
				console.debug("cleared $projectId loader abort controller: ", projectId)
			}
		})

		return
	},
})
